import React, { useMemo } from 'react';
import SVGPatternsDefs from '../visualization/section-view/SVGPatternsDefs';
import {
  buildWallSectionModel,
  type WallBlock,
} from '../visualization/section-view/wallModel';
import {
  buildCeilingSectionModel,
  type CeilingBlock,
} from '../visualization/section-view/ceilingModel';
import {
  BoardRect,
  StructureGroup,
  OrphanInsulationRect,
} from '../visualization/section-view/TechnicalWallDrawing';
import type { LayerV2 } from '../types';

interface MiniSectionPreviewProps {
  layers: LayerV2[];
  className?: string;
  /**
   * Tipo di stratigrafia. Per `ceiling` la preview è renderizzata come sezione
   * ORIZZONTALE (solaio in alto, lastra in basso) — diversa dalla parete che
   * è verticale (lato A sinistra, lato B destra).
   */
  layerType?: 'wall' | 'ceiling';
}

// Default ceiling state per la preview (drop+spacing rappresentativi).
// Serve solo per build del model, non vengono usati per dimensioni grafiche.
const PREVIEW_CEILING_STATE = {
  ceilingDropMm: 200,
  attachmentType: 'suspended' as const,
  hangerSpacingPrimaryMm: 900,
  hangerSpacingPerpendicularMm: 900,
  primarySpacingMm: 1000,
  secondarySpacingMm: 500,
  perimeterProfileMlPerSqm: null,
};

/**
 * Anteprima miniaturizzata della sezione tecnica per le card della lista
 * stratigrafie V2.
 *
 * Riusa le STESSE primitive grafiche della tavola tecnica grande
 * (`BoardRect`, `StructureGroup`, `OrphanInsulationRect`) con lo stesso
 * `wallModel` e gli stessi `SVGPatternsDefs`. Quindi quello che vedi nella
 * card è graficamente identico a quello che vedi quando apri la stratigrafia
 * nel configuratore — solo a dimensione thumbnail e senza header/quote/legenda.
 */
const MiniSectionPreview: React.FC<MiniSectionPreviewProps> = ({
  layers,
  className = '',
  layerType = 'wall',
}) => {
  // Ordine layer per `position`: il join Supabase non garantisce l'ordine, e
  // `buildWallSectionModel` aggrega struct+guida+isolante solo se consecutivi.
  const orderedLayers = useMemo(() => {
    const arr = [...(layers ?? [])];
    arr.sort((a: { position?: number }, b: { position?: number }) => Number(a?.position ?? 0) - Number(b?.position ?? 0));
    return arr;
  }, [layers]);

  // Sempre calcolato per rispettare React Hooks rules (no early return prima dell'hook).
  // Per ceiling il valore non viene usato (rendering delegato a CeilingMiniPreview).
  const model = useMemo(() => buildWallSectionModel(orderedLayers), [orderedLayers]);

  // F17 polish: il ceiling ha layout orizzontale (solaio↑, lastra↓)
  // — render delegato a CeilingMiniPreview con primitive ad hoc.
  if (layerType === 'ceiling') {
    return <CeilingMiniPreview layers={orderedLayers} className={className} />;
  }

  // ===== Layout SVG =====
  // Ratio 4:3 verticale per ogni "fascia parete"; la card della lista ospiterà
  // il SVG con preserveAspectRatio default ("xMidYMid meet") così le proporzioni
  // dei pattern vengono rispettate.
  const VB_W = 240;
  const VB_H = 120;
  const PAD_X = 6;
  const PAD_Y = 8;
  const drawW = VB_W - PAD_X * 2;
  const drawH = VB_H - PAD_Y * 2;

  if (model.blocks.length === 0) {
    return (
      <div
        className={`flex items-center justify-center bg-zinc-50 text-[10px] text-zinc-400 ${className}`}
      >
        Nessun layer
      </div>
    );
  }

  const totalThickness = Math.max(model.totalThicknessMm, 25);

  // Calcolo posizione X dei blocchi
  type DrawBlock = { block: WallBlock; x: number; w: number };
  const drawBlocks: DrawBlock[] = [];
  let cursor = PAD_X;
  for (const b of model.blocks) {
    const sizeMm = b.kind === 'board' ? b.thicknessMm : b.depthMm;
    const w = (sizeMm / totalThickness) * drawW;
    drawBlocks.push({ block: b, x: cursor, w });
    cursor += w;
  }

  // Numero strutture per badge "Orditura N" se doppia
  const totalStructures = model.blocks.filter(b => b.kind === 'structure').length;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <SVGPatternsDefs />

      {/* sfondo bianco pulito */}
      <rect x={0} y={0} width={VB_W} height={VB_H} fill="#ffffff" />

      {/* === DISEGNO PARETE === */}
      <g>
        {drawBlocks.map(({ block, x, w }, idx) => {
          if (block.kind === 'board') {
            return (
              <BoardRect
                key={idx}
                block={block}
                x={x}
                y={PAD_Y}
                w={w}
                h={drawH}
              />
            );
          }
          if (block.kind === 'structure') {
            const ordinal = drawBlocks
              .slice(0, idx + 1)
              .filter(d => d.block.kind === 'structure').length;
            return (
              <StructureGroup
                key={idx}
                block={block}
                x={x}
                y={PAD_Y}
                w={w}
                h={drawH}
                studsCount={1}
                structureOrdinal={ordinal}
                showOrdinal={totalStructures > 1}
              />
            );
          }
          return (
            <OrphanInsulationRect
              key={idx}
              block={block}
              x={x}
              y={PAD_Y}
              w={w}
              h={drawH}
            />
          );
        })}
      </g>

      {/* === ETICHETTE LATO A / LATO B (sottilissime, ai margini superiori) === */}
      <g>
        <text
          x={PAD_X}
          y={PAD_Y - 1}
          fontSize={6}
          fill="#94A3B8"
          fontFamily="Inter, system-ui, sans-serif"
          letterSpacing="0.1em"
        >
          A
        </text>
        <text
          x={VB_W - PAD_X}
          y={PAD_Y - 1}
          fontSize={6}
          fill="#94A3B8"
          fontFamily="Inter, system-ui, sans-serif"
          textAnchor="end"
          letterSpacing="0.1em"
        >
          B
        </text>
      </g>
    </svg>
  );
};

// ============================================================================
// CeilingMiniPreview — sezione orizzontale (solaio in alto → lastra in basso)
// ============================================================================

const CeilingMiniPreview: React.FC<{ layers: LayerV2[]; className?: string }> = ({
  layers,
  className = '',
}) => {
  const model = useMemo(
    () => buildCeilingSectionModel(layers, PREVIEW_CEILING_STATE),
    [layers],
  );

  const VB_W = 240;
  const VB_H = 120;
  const PAD_X = 6;
  const PAD_Y = 4;
  const drawW = VB_W - PAD_X * 2;
  const drawH = VB_H - PAD_Y * 2;

  if (model.blocks.length <= 1) {
    // model.blocks[0] è sempre lo slab datum → ≤1 = stratigrafia vuota
    return (
      <div
        className={`flex items-center justify-center bg-zinc-50 text-[10px] text-zinc-400 ${className}`}
      >
        Controsoffitto vuoto
      </div>
    );
  }

  const totalDepth = Math.max(model.totalDepthMm, 25);

  // Calcola altezze Y per ogni blocco (top→bottom = solaio→lastra)
  type CeilingDrawBlock = { block: CeilingBlock; y: number; h: number };
  const drawBlocks: CeilingDrawBlock[] = [];
  let cursorY = PAD_Y;
  for (const b of model.blocks) {
    const sizeMm =
      b.kind === 'slab' ? Math.max(b.thicknessMm, 8) :
      b.kind === 'hanger_space' ? b.depthMm :
      b.kind === 'frame' ? b.depthMm :
      b.kind === 'ceiling_insulation' ? b.depthMm :
      b.kind === 'board' ? b.thicknessMm : 0;
    const h = (sizeMm / (totalDepth + 8)) * drawH; // +8 per lo slab visivo
    drawBlocks.push({ block: b, y: cursorY, h });
    cursorY += h;
  }

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x={0} y={0} width={VB_W} height={VB_H} fill="#ffffff" />

      {drawBlocks.map((db, idx) => (
        <CeilingBlockRect
          key={idx}
          block={db.block}
          x={PAD_X}
          y={db.y}
          w={drawW}
          h={Math.max(db.h, 2)}
        />
      ))}

      {/* etichette discrete */}
      <text x={PAD_X} y={PAD_Y - 0.5} fontSize={6} fill="#94A3B8" fontFamily="Inter, system-ui">
        SOLAIO
      </text>
      <text
        x={VB_W - PAD_X}
        y={VB_H - 1}
        fontSize={6}
        fill="#94A3B8"
        fontFamily="Inter, system-ui"
        textAnchor="end"
      >
        VISTA
      </text>
    </svg>
  );
};

const CeilingBlockRect: React.FC<{
  block: CeilingBlock;
  x: number;
  y: number;
  w: number;
  h: number;
}> = ({ block, x, y, w, h }) => {
  if (block.kind === 'slab') {
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} fill="#cbd5e1" />
        <line x1={x} y1={y + h} x2={x + w} y2={y + h} stroke="#475569" strokeWidth={0.5} strokeDasharray="2 2" />
      </g>
    );
  }
  if (block.kind === 'hanger_space') {
    // camera tecnica: bianco con sottili linee pendinatura
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} fill="#f8fafc" />
        {[0.2, 0.4, 0.6, 0.8].map((p, i) => (
          <line key={i} x1={x + w * p} y1={y} x2={x + w * p} y2={y + h} stroke="#94a3b8" strokeWidth={0.4} />
        ))}
      </g>
    );
  }
  if (block.kind === 'frame') {
    const color = block.level === 'primary' ? '#71717a' : '#a1a1aa';
    return <rect x={x} y={y} width={w} height={h} fill={color} />;
  }
  if (block.kind === 'ceiling_insulation') {
    return <rect x={x} y={y} width={w} height={h} fill="#fde68a" opacity={0.7} />;
  }
  if (block.kind === 'board') {
    const c = boardKindColor(block.materialKind);
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} fill={c} stroke="#d4d4d8" strokeWidth={0.3} />
      </g>
    );
  }
  return null;
};

function boardKindColor(kind: string): string {
  switch (kind) {
    case 'board_standard': return '#f3f4f6';
    case 'board_fire':     return '#fca5a5';
    case 'board_hydro':    return '#a7f3d0';
    case 'board_high_density': return '#bae6fd';
    case 'board_cement':   return '#9ca3af';
    case 'board_glasroc':  return '#fde68a';
    case 'board_perforated': return '#e5e7eb';
    default: return '#e5e7eb';
  }
}

export default MiniSectionPreview;
